import React from 'react';
import './LandingPage.css';
import headerLogo from '../../assets/차트트레이너.png';

interface LandingPageProps {
  theme: 'light' | 'dark';
  onGetStarted: () => void;
  onLogin: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  theme,
  onGetStarted,
  onLogin,
}) => {
  return (
    <div className={`landing-page ${theme}-theme`}>
      {/* Hero 섹션 */}
      <div className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-logo">
            <img src={headerLogo} alt="차트 트레이너" className="hero-logo-main" />
          </div>
          <h1 className="landing-hero-title">
            차트 트레이너에 오신 것을 환영합니다
          </h1>
          <p className="landing-hero-description">
            실전 투자 경험을 쌓고 AI 분석으로 투자 실력을 향상시키세요
          </p>
          
          {/* 통계 배지 */}
          <div className="landing-hero-stats">
            <div className="hero-stat-item">
              <div className="hero-stat-value">베타</div>
              <div className="hero-stat-label">테스트 진행 중</div>
            </div>
            <div className="hero-stat-item">
              <div className="hero-stat-value">무료</div>
              <div className="hero-stat-label">가입 시 토큰 지급</div>
            </div>
            <div className="hero-stat-item">
              <div className="hero-stat-value">실시간</div>
              <div className="hero-stat-label">AI 분석 제공</div>
            </div>
          </div>

          <div className="landing-hero-cta">
            <button className="landing-primary-btn large" onClick={onGetStarted}>
              무료로 시작하기
              <span className="cta-subtext">가입 시 AI 분석 토큰 1개 지급</span>
            </button>
            <button className="landing-login-btn" onClick={onLogin}>
              로그인
            </button>
          </div>
        </div>
      </div>

      {/* 주요 기능 섹션 */}
      <section className="landing-features">
        <div className="landing-container">
          <h2 className="landing-section-title">주요 기능</h2>
          <div className="landing-features-grid">
            <div className="landing-feature-card feature-card-highlight">
              <span className="feature-card-badge">인기</span>
              <span className="feature-card-icon">📊</span>
              <h3 className="feature-card-title">실전 차트 트레이딩</h3>
              <p className="feature-card-description">
                실제 주식 차트를 기반으로 한 <strong>실전 투자 시뮬레이션</strong>을 경험하세요. 
                다양한 기술적 분석 도구를 활용하여 매매 전략을 수립하고 실행할 수 있습니다.
              </p>
            </div>
            <div className="landing-feature-card">
              <span className="feature-card-icon">🤖</span>
              <h3 className="feature-card-title">AI 투자 분석</h3>
              <p className="feature-card-description">
                최신 AI 기술로 당신의 매매 패턴을 분석하고 <strong>개인 맞춤형 피드백</strong>을 제공합니다. 
                투자 스타일, 리스크 관리, 자금 관리 등 다양한 측면에서 개선점을 제시합니다.
              </p>
            </div>
            <div className="landing-feature-card">
              <span className="feature-card-icon">📈</span>
              <h3 className="feature-card-title">상세 통계 분석</h3>
              <p className="feature-card-description">
                승률, 수익률, 샤프 비율 등 <strong>다양한 투자 지표</strong>를 시각화하여 제공합니다. 
                시간에 따른 성과 추이를 그래프로 확인하고 투자 실력을 객관적으로 평가하세요.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI 섹션 */}
      <section className="landing-ai-section">
        <div className="landing-container">
          <div className="landing-ai-content">
            <div className="landing-ai-text">
              <span className="ai-section-badge">🤖 AI 분석</span>
              <h2 className="landing-section-title ai-section-title-inline" style={{ textAlign: 'left', marginBottom: '1rem' }}>
                <span className="ai-title-highlight">AI가 분석하는</span> <span style={{ whiteSpace: 'nowrap' }}>당신의 투자 패턴</span>
              </h2>
              <p className="landing-ai-description">
                최신 AI 모델이 당신의 매매 내역을 종합 분석하여 투자 스타일, 강점, 개선점을 
                상세히 제시합니다. 전문가의 조언처럼 구체적이고 실용적인 피드백을 받아보세요.
              </p>
              <div className="landing-ai-features">
                <div className="ai-feature-item">
                  <div className="ai-feature-check">✓</div>
                  <span>투자 스타일 자동 분석</span>
                </div>
                <div className="ai-feature-item">
                  <div className="ai-feature-check">✓</div>
                  <span>리스크 관리 평가</span>
                </div>
                <div className="ai-feature-item">
                  <div className="ai-feature-check">✓</div>
                  <span>개인 맞춤형 개선 방안 제시</span>
                </div>
              </div>
            </div>
            <div className="landing-ai-visual">
              <div className="ai-visual-card">
                <div className="ai-visual-header">
                  <div className="ai-visual-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="ai-visual-title">📊 AI 분석 결과</span>
                </div>
                <div className="ai-visual-content">
                  {/* 점수 카드 섹션 */}
                  <div className="score-section-highlight">
                    <div className="score-grid-large">
                      <div className="score-card-large">
                        <div className="score-icon">🛡️</div>
                        <div className="score-card-content">
                          <div className="score-label-large">리스크 관리</div>
                          <div className="score-value-group">
                            <div className="score-value-large">85</div>
                            <div className="score-subtitle">/ 100</div>
                          </div>
                          <div className="score-bar-large">
                            <div className="score-bar-fill-large" style={{ width: '85%' }}></div>
                          </div>
                        </div>
                      </div>
                      <div className="score-card-large">
                        <div className="score-icon">💰</div>
                        <div className="score-card-content">
                          <div className="score-label-large">자금 관리</div>
                          <div className="score-value-group">
                            <div className="score-value-large">78</div>
                            <div className="score-subtitle">/ 100</div>
                          </div>
                          <div className="score-bar-large">
                            <div className="score-bar-fill-large" style={{ width: '78%' }}></div>
                          </div>
                        </div>
                      </div>
                      <div className="score-card-large">
                        <div className="score-icon">📈</div>
                        <div className="score-card-content">
                          <div className="score-label-large">일관성</div>
                          <div className="score-value-group">
                            <div className="score-value-large">92</div>
                            <div className="score-subtitle">/ 100</div>
                          </div>
                          <div className="score-bar-large">
                            <div className="score-bar-fill-large" style={{ width: '92%' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 종합 평가 */}
                  <div className="feedback-block feedback-block-summary" style={{ marginTop: '1.5rem' }}>
                    <h3 className="feedback-block-title">📝 종합 평가</h3>
                    <p className="feedback-block-content">
                      안정적인 투자 스타일을 보이고 있으며, 리스크 관리와 자금 배분이 우수합니다. 
                      일관성 있는 매매 패턴이 돋보입니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 토큰 섹션 */}
      <section className="landing-token-section">
        <div className="landing-container">
          <h2 className="landing-section-title">AI 분석 토큰</h2>
          <p className="landing-section-subtitle">
            토큰을 사용하여 AI 분석을 받아보세요
          </p>
          <div className="landing-token-grid">
            <div className="token-card">
              <span className="token-card-icon">🎮</span>
              <h3 className="token-card-title">게임 플레이</h3>
              <p className="token-card-description">
                게임을 플레이하여 <strong>매매 내역을 쌓아보세요</strong>. 
                더 많은 거래 내역을 쌓을수록 더 상세한 AI 분석을 받을 수 있습니다.
              </p>
            </div>
            <div className="token-card">
              <span className="token-card-icon">🪙</span>
              <h3 className="token-card-title">토큰 사용</h3>
              <p className="token-card-description">
                토큰 1개로 <strong>최근 10개 세션</strong>에 대한 종합 AI 분석을 받을 수 있습니다. 
                매매 패턴을 깊이 있게 분석받아 투자 실력을 향상시키세요.
              </p>
            </div>
            <div className="token-card">
              <span className="token-card-icon">🎁</span>
              <h3 className="token-card-title">가입 보너스</h3>
              <p className="token-card-description">
                신규 가입 시 <strong>AI 분석 토큰 1개</strong>를 무료로 지급합니다. 
                바로 시작해서 첫 분석을 받아보세요!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 커뮤니티 기능 섹션 */}
      <section className="landing-community-section">
        <div className="landing-container">
          <h2 className="landing-section-title">추가 기능</h2>
          <p className="landing-section-subtitle">
            차트 트레이너만의 특별한 기능들을 만나보세요
          </p>
          <div className="landing-community-grid">
            <div className="community-feature-card">
              <span className="community-feature-icon">🏆</span>
              <h3 className="community-feature-title">랭킹 시스템</h3>
              <p className="community-feature-description">
                다른 사용자들과 <strong>수익률을 비교</strong>하고 순위를 확인하세요. 
                상위 랭커들의 전략을 참고하여 자신만의 투자 스타일을 발전시킬 수 있습니다.
              </p>
              <div className="community-feature-list">
                <div className="community-feature-item">
                  <div className="feature-check">✓</div>
                  <span>전체 랭킹 확인</span>
                </div>
                <div className="community-feature-item">
                  <div className="feature-check">✓</div>
                  <span>상위 랭커 프로필 조회</span>
                </div>
              </div>
            </div>
            <div className="community-feature-card">
              <span className="community-feature-icon">📋</span>
              <h3 className="community-feature-title">매매 내역 관리</h3>
              <p className="community-feature-description">
                모든 거래 내역을 <strong>체계적으로 관리</strong>하고 분석하세요. 
                세션별, 종목별로 상세한 통계를 확인할 수 있습니다.
              </p>
              <div className="community-feature-list">
                <div className="community-feature-item">
                  <div className="feature-check">✓</div>
                  <span>세션별 거래 내역 조회</span>
                </div>
                <div className="community-feature-item">
                  <div className="feature-check">✓</div>
                  <span>종목별 성과 분석</span>
                </div>
              </div>
            </div>
            <div className="community-feature-card">
              <span className="community-feature-icon">🎯</span>
              <h3 className="community-feature-title">업적 시스템</h3>
              <p className="community-feature-description">
                다양한 <strong>업적과 칭호</strong>를 달성하며 게임의 재미를 더하세요. 
                특정 조건을 달성하면 특별한 칭호를 획득할 수 있습니다.
              </p>
              <div className="community-feature-list">
                <div className="community-feature-item">
                  <div className="feature-check">✓</div>
                  <span>다양한 업적 달성</span>
                </div>
                <div className="community-feature-item">
                  <div className="feature-check">✓</div>
                  <span>칭호 수집</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 최종 CTA 섹션 */}
      <section className="landing-final-cta">
        <div className="landing-container">
          <h2 className="landing-cta-title">지금 바로 시작하세요</h2>
          <p className="landing-cta-description">
            무료로 가입하고 실전 투자 경험을 쌓아보세요. AI 분석으로 투자 실력을 한 단계 업그레이드하세요.
          </p>
          <div className="landing-cta-button-wrapper">
            <button className="landing-primary-btn large" onClick={onGetStarted}>
              무료로 시작하기
              <span className="cta-subtext">가입 시 AI 분석 토큰 1개 지급</span>
            </button>
          </div>
        </div>
      </section>

      {/* 푸터 */}
      <footer className="landing-footer">
      </footer>
    </div>
  );
};
